/**
 * Hover swap for `component-milb-button`, on by default for any button with an icon.
 *
 * The button's icon and label trade places on hover. The animation itself is
 * CSS (see component-milb-button.css); the only thing that cannot be expressed
 * there is how far each one travels — the icon moves the label's width plus the
 * gap, the label moves the icon's width plus the gap, and CSS can read neither
 * width. "Shop all products" is a different length in every locale and the icon
 * is 12px on mobile against 14px on desktop, so there is no constant to write.
 * This measures the two and publishes them as custom properties.
 *
 * Delegated from the document rather than written as a custom element wrapping
 * each button. Twelve sections render this snippet, several inside markup the
 * theme editor and the variant picker re-render wholesale — the PDP replaces
 * its whole add-to-cart region on a variant change — so a listener bound to a
 * button, or an element wrapping one, would go quiet on the swap. It also keeps
 * the snippet's markup contract unchanged for the consumers that do not opt in.
 *
 * Measured on pointer entry rather than up front, which makes it correct
 * regardless of re-renders, resizes, locale or when the webfont finishes
 * loading, and costs two reads on a hover the shopper already paid for.
 *
 * Bound at module scope with no registration guard, because it does not need
 * one: every consumer loads this with `type="module"`, and a module is keyed by
 * URL in the module map, so the body below runs exactly once however many
 * sections include the tag and however often the theme editor re-renders them.
 * That is the same "no-op after the first load" property `customElements.define`
 * relies on elsewhere in the theme.
 */

/**
 * Publishes the swap distances for one button as custom properties.
 * @param {HTMLElement} button A `.milb-button--swap`
 */
function measureSwap(button) {
  const icon = button.querySelector('.milb-button__icon');
  const label = button.querySelector('.milb-button__label');
  if (!icon || !label) return;

  /* Widths, never positions. The hover only translates these two, and a
     translation moves a box without resizing it, so what is measured here is
     the same whether or not the swap is currently applied — there is no
     feedback loop. Reading their positions mid-hover would be a different
     story, which is why nothing here does.
     Sub-pixel rects rather than offsetWidth, whose rounding to whole pixels
     showed up as the gap growing by a third of a pixel on hover. */
  const gap = parseFloat(getComputedStyle(button).columnGap) || 0;

  button.style.setProperty('--milb-button-icon-shift', `${label.getBoundingClientRect().width + gap}px`);
  button.style.setProperty('--milb-button-label-shift', `${icon.getBoundingClientRect().width + gap}px`);
}

document.addEventListener(
  'pointerover',
  (event) => {
    const button = event.target.closest?.('.milb-button--swap');
    if (button) measureSwap(button);
  },
  { passive: true },
);
